// ✅ src/pages/Asset/AssetScrapListing.jsx
// Backend mount:
//   app.use("/api/scrapListing", scrapListing);
//
// Expected endpoints:
//   GET  http://nissi-india.com/api/scrapListing/branches
//   GET  http://nissi-india.com/api/scrapListing/subtypes
//   GET  http://nissi-india.com/api/scrapListing/list?fromDate=dd/MM/yyyy&toDate=dd/MM/yyyy&location=&assetSubType=&page=&pageSize=

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaEye, FaChevronDown, FaChevronUp } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/scrapListing";

const AssetScrapListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    location: "",
    assetSubType: "",
  });

  const [branches, setBranches] = useState([]);
  const [subtypes, setSubtypes] = useState([]);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // ✅ safe localStorage getter
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ default dates (last 7 days)
  const getDefaultDates = () => {
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) =>
      `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

    const now = new Date();
    const prev = new Date();
    prev.setDate(now.getDate() - 7);

    return { fromDate: fmt(prev), toDate: fmt(now) };
  };

  useEffect(() => {
    setFilters((p) => ({ ...p, ...getDefaultDates() }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeBranch = (b) => {
    const raw = b?.raw || b || {};
    const value =
      b?.value ??
      raw?.fld_branchid ??
      raw?.BranchId ??
      raw?.branchid ??
      "";
    const label =
      (b?.label && String(b.label).trim()) ||
      raw?.fld_branchName ||
      raw?.fld_branchname ||
      raw?.BranchName ||
      raw?.branchName ||
      (value ? `Branch ${value}` : "");

    return {
      value: String(value),
      label: String(label),
      raw,
    };
  };

  const normalizeSubtype = (s) => {
    const raw = s?.raw || s || {};
    const value =
      s?.value ??
      s?.id ??
      raw?.fld_AssetSubTypeid ??
      raw?.AssetSubTypeId ??
      raw?.assetSubTypeId ??
      "";
    const label =
      (s?.label && String(s.label).trim()) ||
      s?.name ||
      raw?.fld_AssetSubTypeName ||
      raw?.AssetSubTypeName ||
      raw?.assetSubTypeName ||
      (value ? String(value) : "");

    return {
      value: String(value),
      label: String(label),
      raw,
    };
  };

  const normalizeRow = (r, idx, page, pageSize) => {
    const raw = r?.raw || r || {};

    const id =
      r?.id ??
      raw?.id ??
      raw?.ID ??
      raw?.fld_ScrapMasId ??
      raw?.fld_transid ??
      raw?.TranID ??
      `${page}-${idx}`;

    const sNo =
      r?.sNo ??
      raw?.sNo ??
      raw?.SNo ??
      (page - 1) * pageSize + idx + 1;

    const assetSubType =
      r?.assetSubType ??
      raw?.assetSubType ??
      raw?.AssetSubType ??
      raw?.fld_AssetSubTypeName ??
      "";

    const assetIdCode =
      r?.assetIdCode ??
      raw?.assetIdCode ??
      raw?.AssetIdCode ??
      raw?.fld_idcode ??
      raw?.fld_AssetIDCode ??
      "";

    const branch =
      r?.branch ??
      raw?.branch ??
      raw?.Branch ??
      raw?.fld_BranchName ??
      raw?.fld_branchname ??
      raw?.BranchName ??
      "";

    const scrapDate =
      r?.scrapDate ??
      raw?.scrapDate ??
      raw?.ScrapDate ??
      raw?.fld_date ??
      raw?.fld_ScrapDate ??
      "";

    const scrapReason =
      r?.scrapReason ??
      raw?.scrapReason ??
      raw?.ScrapReason ??
      raw?.fld_reason ??
      "";

    const tranId =
      r?.tranId ??
      raw?.fld_transid ??
      raw?.TranID ??
      raw?.tranId ??
      id;

    return {
      id,
      sNo,
      assetSubType,
      assetIdCode,
      branch,
      scrapDate,
      scrapReason,
      tranId,
      raw,
    };
  };

  // ✅ load dropdowns
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [bRes, sRes] = await Promise.all([
          axios.get(`${API_BASE}/branches`, { headers: commonHeaders }),
          axios.get(`${API_BASE}/subtypes`, { headers: commonHeaders }),
        ]);

        const b = bRes?.data?.data || [];
        const s = sRes?.data?.data || [];

        setBranches(b.map(normalizeBranch));
        setSubtypes(s.map(normalizeSubtype));
      } catch (err) {
        console.error(err);
        toast.error("Failed to load dropdowns");
      }
    };

    loadDropdowns();
  }, [commonHeaders]);

  const fetchListing = async (page = 1) => {
    try {
      setLoading(true);

      const params = {
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        location: filters.location,
        assetSubType: filters.assetSubType,
        page,
        pageSize: recordsPerPage,
      };

      const res = await axios.get(`${API_BASE}/list`, {
        params,
        headers: commonHeaders,
      });

      const data = res?.data?.data || [];
      const pag = res?.data?.pagination;

      setRecords(
        data.map((r, idx) => normalizeRow(r, idx, page, recordsPerPage))
      );

      if (pag) {
        setCurrentPage(pag.page || page);
        setTotalPages(pag.totalPages || 1);
        setTotalRecords(pag.total ?? data.length);
      } else {
        setCurrentPage(page);
        setTotalPages(1);
        setTotalRecords(data.length);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to load scrap listing"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filters.fromDate && filters.toDate) {
      fetchListing(1);
    }
  }, [filters.fromDate, filters.toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddNew = () => {
    navigate("/Asset/AssetScrapEntry");
  };

  const handlePrint = (record) => {
    const tranId = record.tranId || record.id;
    if (!tranId) {
      toast.error("Tran ID not found for print");
      return;
    }

    window.open(
      `/Asset/AssetScrapPrint?tranId=${encodeURIComponent(tranId)}`,
      "_blank",
      "width=900,height=900,scrollbars=yes,resizable=no"
    );
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleViewClick = () => {
    fetchListing(1);
  };

  // ✅ Clear filters back to default date range and reload
  const handleClearFilters = () => {
    setFilters({
      ...getDefaultDates(),
      location: "",
      assetSubType: "",
    });
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchListing(page);
  };

  const renderPageNumbers = useMemo(() => {
    const pages = [];
    const maxVisiblePages = 10;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = 1; i <= maxVisiblePages; i++) pages.push(i);
      pages.push("...");
    }

    return pages;
  }, [totalPages]);

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET SCRAP</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Asset Scrap Listing
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleViewClick}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaEye />
                    {loading ? "Loading..." : "View"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="dd/MM/yyyy"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="dd/MM/yyyy"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Location
                  </label>
                  <select
                    value={filters.location}
                    onChange={(e) => handleFilterChange("location", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {branches.map((b, idx) => (
                      <option key={idx} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Asset Sub Type
                  </label>
                  <select
                    value={filters.assetSubType}
                    onChange={(e) => handleFilterChange("assetSubType", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {subtypes.map((s, idx) => (
                      <option key={idx} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Asset SubType</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Asset ID/Code</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Branch</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Scrap Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Scrap Reason</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Print</th>
                </tr>
              </thead>

              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {loading ? (
                  <tr>
                    <td colSpan={7} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan={7} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      No records found
                    </td>
                  </tr>
                ) : (
                  records.map((record, index) => (
                    <tr
                      key={record.id ?? index}
                      className={`${
                        index % 2 === 0
                          ? isDark ? "bg-gray-800" : "bg-white"
                          : isDark ? "bg-gray-750" : "bg-gray-50"
                      } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                    >
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {record.sNo}
                      </td>

                      <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                        {record.assetSubType}
                      </td>

                      <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                        {record.assetIdCode}
                      </td>

                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {record.branch}
                      </td>

                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {record.scrapDate}
                      </td>

                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {record.scrapReason}
                      </td>

                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handlePrint(record)}
                          className={`px-3 py-1 rounded text-sm font-medium transition-all duration-200 ${
                            isDark
                              ? "bg-blue-700 hover:bg-blue-600 text-white"
                              : "bg-blue-600 hover:bg-blue-700 text-white"
                          }`}
                        >
                          Print
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ==================== PAGINATION ==================== */}
          <div className="flex justify-between items-center mt-6 flex-wrap gap-3">
            <div className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm`}>
              Total: <span className="font-semibold">{totalRecords}</span>
            </div>

            <div className="flex gap-1">
              {renderPageNumbers.map((page, index) => (
                <button
                  key={index}
                  onClick={() => page !== "..." && handlePageChange(page)}
                  disabled={page === "..."}
                  className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                    page === currentPage
                      ? "bg-blue-600 text-white border-blue-600"
                      : page === "..."
                      ? isDark
                        ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                        : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                      : isDark
                      ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetScrapListing;